import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { useNavigate } from 'react-router-dom';
import { Layout, ErrorNotice } from '../components/Layout';
import { useCapture } from '../context/capture';
import { cropAndCompress, normalizeHeic, sha256Hex } from '../lib/image-editor';
import { api } from '../lib/api';
import { Turnstile } from '../components/Turnstile';

export function ImageEditorPage() {
  const navigate = useNavigate();
  const { file, templateType } = useCapture();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [uploadProgress, setUploadProgress] = useState('');
  const receiveTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const isPdf = Boolean(
    file &&
    /\.pdf$/i.test(file.name) &&
    (file.type === 'application/pdf' ||
      file.type === '' ||
      file.type === 'application/octet-stream'),
  );

  useEffect(() => {
    if (!file) {
      navigate('/capture', { replace: true });
      return;
    }
    let active = true;
    let url = '';
    const preview = isPdf
      ? import('../lib/pdf-preview').then(({ renderPdfFirstPage }) => renderPdfFirstPage(file))
      : normalizeHeic(file);
    void preview
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      })
      .catch(() =>
        setError(
          isPdf
            ? 'ไม่สามารถอ่านหน้าแรกของ PDF ได้ กรุณาตรวจสอบว่าไฟล์ไม่เสียหายหรือมีรหัสผ่าน'
            : 'ไม่สามารถแปลงไฟล์ HEIC ได้ กรุณาลองเลือกรูปอื่น',
        ),
      );
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [file, isPdf, navigate]);

  const confirm = async () => {
    if (!imageUrl || !area) return;
    setBusy(true);
    setError('');
    try {
      const blob = await cropAndCompress(imageUrl, area, rotation);
      if (blob.size > 10 * 1024 * 1024) throw new Error('รูปหลังบีบอัดยังใหญ่เกิน 10 MB');
      const created = await api<{ data: { id: string } }>('/api/appointments', {
        method: 'POST',
        body: JSON.stringify({ timezone: 'Asia/Bangkok', templateType }),
      });
      const form = new FormData();
      form.set('image', new File([blob], 'appointment.webp', { type: 'image/webp' }));
      if (file && isPdf) form.set('sourceFile', file, file.name);
      form.set('clientSha256', await sha256Hex(blob));
      await api(`/api/appointments/${created.data.id}/image`, { method: 'POST', body: form });
      if (file && isPdf) {
        const { renderPdfPages } = await import('../lib/pdf-preview');
        await renderPdfPages(
          file,
          async ({ pageNumber, totalPages, sourcePages, blob: pageBlob }) => {
            setUploadProgress(`กำลังเตรียม PDF หน้า ${pageNumber}/${totalPages}`);
            const pageForm = new FormData();
            const extension = pageBlob.type === 'image/png' ? 'png' : 'webp';
            pageForm.set(
              'page',
              new File([pageBlob], `page-${pageNumber}.${extension}`, { type: pageBlob.type }),
            );
            pageForm.set('totalPages', String(totalPages));
            pageForm.set('sourcePages', String(sourcePages));
            await api(`/api/appointments/${created.data.id}/pages/${pageNumber}`, {
              method: 'POST',
              body: pageForm,
            });
          },
        );
      }
      await api(`/api/appointments/${created.data.id}/extract`, {
        method: 'POST',
        body: JSON.stringify({ turnstileToken }),
      });
      navigate(`/appointments/${created.data.id}/processing`, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'อัปโหลดไฟล์เอกสารไม่สำเร็จ กรุณาลองใหม่',
      );
    } finally {
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
      setUploadProgress('');
      setBusy(false);
    }
  };

  return (
    <Layout title="ครอบตัดและหมุนภาพ">
      <section className="mx-auto max-w-2xl space-y-4">
        <div className="relative h-[55vh] min-h-80 overflow-hidden rounded-2xl bg-slate-900">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={3 / 4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropAreaChange={(_, pixels) => setArea(pixels)}
              onCropComplete={(_, pixels) => setArea(pixels)}
            />
          )}
        </div>
        <div className="card space-y-4">
          <label className="label" htmlFor="zoom">
            ขยายภาพ
          </label>
          <input
            id="zoom"
            className="w-full accent-teal-700"
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRotation((value) => value - 90)}
            >
              ↶ หมุนซ้าย
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRotation((value) => value + 90)}
            >
              ↷ หมุนขวา
            </button>
          </div>
        </div>
        {error && <ErrorNotice message={error} />}
        <Turnstile onToken={receiveTurnstileToken} resetKey={turnstileReset} />
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/capture')}
            disabled={busy}
          >
            ถ่ายใหม่
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void confirm()}
            disabled={busy || !area || !turnstileToken}
          >
            {busy ? uploadProgress || 'กำลังอัปโหลด…' : 'ใช้ไฟล์เอกสารนี้'}
          </button>
        </div>
      </section>
    </Layout>
  );
}
